import React, { useCallback, useEffect, useMemo, useState } from "react";
import { listLendingPadOrganizations, searchOrganizations } from "../api";
import { EFFECTIVE_STATE_OPTIONS, shortCompanyId } from "../constants";
import { OrgAvatar } from "./OrgAvatar";
import { StatusBadge } from "./StatusBadge";

function orgName(org) {
  return (
    org?.name ||
    org?.organizationName ||
    org?.displayName ||
    org?.orgName ||
    org?.id
  );
}

// Rows come one per LendingPad company. A selection without a company (a
// recent, a fresh org) means the organization's primary one.
function isSelectedRow(org, selectedOrgId, selectedCompanyId) {
  if (org.id !== selectedOrgId) {
    return false;
  }

  return !selectedCompanyId || org.companyId === selectedCompanyId;
}

function orgImage(org) {
  return org?.image || org?.logoUrl || org?.logo || org?.coverPhoto || null;
}

export function FleetTable({
  env,
  selectedOrgId,
  selectedCompanyId,
  onSelectOrg,
  refreshKey,
  recents = [],
}) {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [effectiveState, setEffectiveState] = useState("");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [directoryResults, setDirectoryResults] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);

  const loadOrganizations = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      setLoading(true);
      setError(null);

      try {
        const json = await listLendingPadOrganizations(env, {
          limit: 25,
          cursor,
          effectiveState,
          attentionOnly: attentionOnly ? "true" : "",
          search: search.trim(),
        });

        if (!json?.success) {
          throw new Error(json?.reason || "Failed to load organizations");
        }

        const rows = Array.isArray(json.organizations) ? json.organizations : [];
        setOrganizations((current) => (append ? [...current, ...rows] : rows));
        setNextCursor(json.nextCursor || null);
      } catch (loadError) {
        setError(loadError?.message || "Failed to load organizations");
      } finally {
        setLoading(false);
      }
    },
    [env, effectiveState, attentionOnly, search],
  );

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations, refreshKey, reloadKey]);

  // Debounced directory search across ALL organizations — surfaces orgs that
  // have no LendingPad connection yet so they can be set up / activated here.
  useEffect(() => {
    const query = search.trim();
    if (query.length < 2) {
      setDirectoryResults([]);
      setDirectoryLoading(false);
      return undefined;
    }

    let cancelled = false;
    setDirectoryLoading(true);
    const timer = setTimeout(() => {
      searchOrganizations(env, query)
        .then((json) => {
          if (cancelled) {
            return;
          }
          const orgs = Array.isArray(json?.orgs)
            ? json.orgs
            : Array.isArray(json?.organizations)
              ? json.organizations
              : [];
          setDirectoryResults(orgs);
        })
        .catch(() => {
          if (!cancelled) {
            setDirectoryResults([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setDirectoryLoading(false);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, env]);

  const filteredOrganizations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return organizations;
    }

    return organizations.filter((org) => {
      const haystack = [
        org.id,
        org.name,
        org.company,
        org.companyId,
        org.alias,
        org.systemAdminUserId,
        org.effectiveState,
        org.lastError,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [organizations, search]);

  const showRecents = !search.trim() && recents.length > 0;

  // Directory matches that are not already in the LendingPad list — i.e. orgs
  // with no connection yet. Shown as a separate "Not connected yet" group.
  const unconnectedResults = useMemo(() => {
    if (!search.trim()) {
      return [];
    }
    const connectedIds = new Set(organizations.map((org) => org.id));
    const seen = new Set();
    return directoryResults.filter((org) => {
      if (!org?.id || connectedIds.has(org.id) || seen.has(org.id)) {
        return false;
      }
      seen.add(org.id);
      return true;
    });
  }, [directoryResults, organizations, search]);

  const hasNoResults =
    filteredOrganizations.length === 0 && unconnectedResults.length === 0;

  return (
    <div className="integrations-card">
      <div className="integrations-card-header">
        <div>
          <h2 className="integrations-card-title">Organizations</h2>
          <p className="integrations-card-subtitle">
            Find an organization to manage its LendingPad connection.
          </p>
        </div>
        <button
          type="button"
          className="integrations-btn integrations-btn-ghost"
          onClick={() => setReloadKey((value) => value + 1)}
          disabled={loading}
        >
          <span className="integrations-btn-ico" aria-hidden="true">
            ↻
          </span>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="integrations-finder">
        <div
          className={`integrations-search ${search ? "has-value" : ""}`}
        >
          <span className="integrations-search-ico" aria-hidden="true">
            ⌕
          </span>
          <input
            className="integrations-search-input"
            placeholder="Search by name or Organization ID…"
            value={search}
            autoComplete="off"
            onChange={(event) => setSearch(event.target.value)}
          />
          {search ? (
            <button
              type="button"
              className="integrations-search-clear"
              aria-label="Clear search"
              onClick={() => setSearch("")}
            >
              ✕
            </button>
          ) : null}
        </div>

        {showRecents ? (
          <div className="integrations-recents">
            <div className="integrations-recents-label">
              <span aria-hidden="true">🕘</span> Recently viewed
            </div>
            <div className="integrations-recent-chips">
              {recents.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  className="integrations-recent-chip"
                  onClick={() => onSelectOrg(org)}
                >
                  <OrgAvatar
                    name={org.name}
                    id={org.id}
                    image={orgImage(org)}
                    className="integrations-recent-logo"
                  />
                  {org.name || org.id}
                  <span
                    className={`integrations-recent-tick ${org.effectiveState || "pending"}`}
                  />
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="integrations-filter-row">
        <select
          className="integrations-select"
          value={effectiveState}
          onChange={(event) => setEffectiveState(event.target.value)}
        >
          {EFFECTIVE_STATE_OPTIONS.map((option) => (
            <option key={option.value || "all"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label className="integrations-toggle-pill">
          <input
            type="checkbox"
            checked={attentionOnly}
            onChange={(event) => setAttentionOnly(event.target.checked)}
          />
          Needs attention
        </label>
      </div>

      {error ? <div className="integrations-result err">{error}</div> : null}

      <div className="integrations-fleet-list">
        {hasNoResults ? (
          <div className="integrations-fleet-empty">
            <div className="integrations-fleet-empty-ico" aria-hidden="true">
              ⌕
            </div>
            <p>
              {loading || directoryLoading
                ? "Searching organizations…"
                : "No organizations match your search."}
            </p>
          </div>
        ) : (
          <>
            {filteredOrganizations.map((org) => (
              <button
                key={`${org.id}:${org.companyId || ""}`}
                type="button"
                className={`integrations-fleet-row ${
                  isSelectedRow(org, selectedOrgId, selectedCompanyId)
                    ? "selected"
                    : ""
                }`}
                onClick={() => onSelectOrg(org)}
              >
                <OrgAvatar
                  name={org.name}
                  id={org.id}
                  image={orgImage(org)}
                  className="integrations-fleet-logo"
                />
                <span className="integrations-fleet-main">
                  <span className="integrations-fleet-name">
                    {org.name || "Unnamed organization"}
                  </span>
                  <span className="integrations-fleet-sub">
                    <span className="integrations-mono">{org.id}</span>
                    {org.companyId ? (
                      <>
                        {" · "}
                        {org.alias ? `${org.alias} ` : ""}
                        <span
                          className="integrations-mono"
                          title={org.companyId}
                        >
                          {shortCompanyId(org.companyId)}
                        </span>
                      </>
                    ) : null}
                  </span>
                </span>
                <span className="integrations-fleet-right">
                  <StatusBadge state={org.effectiveState} />
                  {/* Soft, specific flags on an otherwise-connected org: a silent
                      or never-switched-on feed is worth checking, but is not a
                      confirmed fault, so the badge stays connected and the row
                      says exactly what to look at rather than a vague alarm. */}
                  {org.feedNeverDelivered ? (
                    <span className="integrations-attention-flag">
                      ⚠ No deliveries yet
                    </span>
                  ) : org.feedSilent ? (
                    <span className="integrations-attention-flag">
                      ⚠ No recent updates
                    </span>
                  ) : org.needsAttention ? (
                    <span className="integrations-attention-flag">
                      ⚠ Needs attention
                    </span>
                  ) : null}
                </span>
              </button>
            ))}

            {unconnectedResults.length > 0 ? (
              <>
                <div className="integrations-fleet-group">
                  Not connected yet. Set up manually
                </div>
                {unconnectedResults.map((org) => (
                  <button
                    key={org.id}
                    type="button"
                    className={`integrations-fleet-row ${
                      org.id === selectedOrgId ? "selected" : ""
                    }`}
                    onClick={() =>
                      onSelectOrg({
                        id: org.id,
                        name: orgName(org),
                        image: orgImage(org),
                      })
                    }
                  >
                    <OrgAvatar
                      name={orgName(org)}
                      id={org.id}
                      image={orgImage(org)}
                      className="integrations-fleet-logo unconnected"
                    />
                    <span className="integrations-fleet-main">
                      <span className="integrations-fleet-name">
                        {orgName(org)}
                      </span>
                      <span className="integrations-fleet-sub">
                        <span className="integrations-mono">{org.id}</span>
                      </span>
                    </span>
                    <span className="integrations-fleet-right">
                      <span className="integrations-badge pending">
                        <span className="integrations-badge-ico" aria-hidden="true">
                          ＋
                        </span>
                        Not connected
                      </span>
                    </span>
                  </button>
                ))}
              </>
            ) : null}
          </>
        )}
      </div>

      <div className="integrations-list-footer">
        <span>
          {filteredOrganizations.length > 0
            ? `Showing ${filteredOrganizations.length} organization${
                filteredOrganizations.length === 1 ? "" : "s"
              }`
            : ""}
        </span>
        {nextCursor ? (
          <button
            type="button"
            className="integrations-btn integrations-btn-ghost"
            disabled={loading}
            onClick={() =>
              loadOrganizations({ cursor: nextCursor, append: true })
            }
          >
            Load more
          </button>
        ) : null}
      </div>
    </div>
  );
}
